import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CustomEncoder, ENVIRONMENT, FeatureFlagsService } from '@iot-platform/core';
import {
  BaseUser,
  BusinessProfile,
  CommonApiListResponse,
  CommonApiRequestBody,
  CommonGenericModel,
  Environment,
  FavoriteView,
  FeatureFlags
} from '@iot-platform/models/common';
import { I4BGrid, I4BGridData, I4BGridOptions } from '@iot-platform/models/grid-engine';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiHelpers } from '@iot-platform/iot-platform-utils';

@Injectable({
  providedIn: 'root'
})
export class FavoriteViewsService {
  protected readonly environment: Environment = inject(ENVIRONMENT);
  protected readonly httpClient: HttpClient = inject(HttpClient);
  protected readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);

  getFavoriteViews(): Observable<FavoriteView[]> {
    return this.httpClient
      .get<CommonApiListResponse<FavoriteView>>(`${this.environment.api.url_v2}${this.environment.api.endpoints?.favoriteViews}`)
      .pipe(map((data: CommonApiListResponse<FavoriteView>) => data.content));
  }

  isNameUnique(name: string, businessProfileId: string, masterview: string, shared: boolean): Observable<boolean> {
    let params: HttpParams = new HttpParams();
    params = params.set('businessProfileId', businessProfileId);
    params = params.set('shared', shared);
    params = params.set('masterview', masterview);
    return this.httpClient
      .get<CommonApiListResponse<FavoriteView>>(`${this.environment.api.url_v2}${this.environment.api.endpoints?.favoriteViews}`, { params })
      .pipe(
        map((data: CommonApiListResponse<FavoriteView>) => data.content.filter((favoriteView) => !!favoriteView.name)),
        map((favoriteViews: FavoriteView[]) => favoriteViews.map((favoriteView) => favoriteView.name?.toLowerCase())),
        map((names: string[]) => !names.includes(name.toLowerCase()))
      );
  }

  saveFavoriteView(favoriteView: FavoriteView): Observable<FavoriteView> {
    return this.httpClient.post<FavoriteView>(`${this.environment.api.url_v2}${this.environment.api.endpoints?.favoriteViews}`, favoriteView);
  }

  updateFavoriteView(favoriteView: FavoriteView): Observable<FavoriteView> {
    return this.httpClient.patch<FavoriteView>(`${this.environment.api.url_v2}${this.environment.api.endpoints?.favoriteViews}/${favoriteView.id}`, {
      name: favoriteView.name,
      pinned: favoriteView.pinned,
      shared: favoriteView.shared,
      color: favoriteView.color,
      filters: favoriteView.filters,
      description: favoriteView.description,
      businessProfileId: favoriteView.businessProfileId,
      gridId: favoriteView.gridId,
      ...(this.isReportExportEnabled(favoriteView.masterView) ? { export: favoriteView.export } : {})
    });
  }

  private isReportExportEnabled(masterView: string | undefined): boolean {
    const isEventsMasterView = masterView === 'asset-events' || masterView === 'device-events';
    return this.featureFlagsService.isFeatureEnabled(
      isEventsMasterView ? FeatureFlags.FavoriteViewReportExportForEvents : FeatureFlags.FavoriteViewReportExport
    );
  }

  deleteFavoriteView(favoriteView: FavoriteView): Observable<FavoriteView> {
    return this.httpClient
      .delete<void>(`${this.environment.api.url_v2}${this.environment.api.endpoints?.favoriteViews}/${favoriteView.id}`)
      .pipe(map((_) => favoriteView));
  }

  getFavoriteViewCount(favoriteView: FavoriteView): Observable<FavoriteView> {
    const params: HttpParams = ApiHelpers.getHttpParams({
      limit: -1, // skip pagination in params, it is included in the body
      filters: [], // skip filters in params, they are included in the body
      variables: [],
      tags: []
    });
    const body: CommonApiRequestBody = ApiHelpers.getHttpRequestBody({ page: 0, limit: 0, filters: favoriteView.filters });

    return this.httpClient
      .post<CommonApiListResponse<CommonGenericModel>>(`${this.environment.api.url}/${favoriteView.masterView}/search`, body, { params })
      .pipe(map((response: CommonApiListResponse<CommonGenericModel>) => ({ ...favoriteView, count: response?.page?.total ?? 0 })));
  }

  getBusinessProfiles(): Observable<BusinessProfile[]> {
    return this.httpClient
      .get<{
        page: unknown;
        content: BusinessProfile[];
      }>(`${this.environment.api.url}${this.environment.api.endpoints?.businessProfiles}`)
      .pipe(map((response) => response.content));
  }

  getBusinessProfileById(businessProfileId: string): Observable<BusinessProfile> {
    return this.httpClient.get<BusinessProfile>(`${this.environment.api.url}${this.environment.api.endpoints?.businessProfiles}/${businessProfileId}`);
  }

  getBusinessProfileUsers(businessProfileId: string): Observable<BaseUser[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('limit', '10000');
    params = params.set('page', '0');
    params = params.append('userStatus', 'Active');
    params = params.append('userStatus', 'Pending');
    params = params.set('withParents', 'true');
    params = params.set('businessProfileId', businessProfileId);

    return this.httpClient
      .get<CommonApiListResponse<BaseUser>>(`${this.environment.api.url}${this.environment.api.endpoints.users}`, { params })
      .pipe(map((response) => response.content));
  }

  getGridsByMasterViewAndBusinessProfileId(masterView: string, businessProfileId: string): Observable<I4BGrid<I4BGridOptions, I4BGridData>[]> {
    let params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });
    params = params.set('businessProfileId', businessProfileId);

    return this.httpClient
      .get<{
        page: unknown;
        content: I4BGrid<I4BGridOptions, I4BGridData>[];
      }>(`${this.environment.api.url}${this.environment.api.endpoints?.grids}/${masterView}`, {
        params
      })
      .pipe(map((response) => response.content));
  }

  getPersonalGridByUserId(masterView: string, userId: string): Observable<I4BGrid<I4BGridOptions, I4BGridData>[]> {
    let params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });
    params = params.set('userId', userId);

    return this.httpClient
      .get<{
        page: unknown;
        content: I4BGrid<I4BGridOptions, I4BGridData>[];
      }>(`${this.environment.api.url}${this.environment.api.endpoints?.grids}/${masterView}`, {
        params
      })
      .pipe(map((response) => response.content));
  }

  getGrids(masterView: string, businessProfileId: string, userId: string): Observable<I4BGrid<I4BGridOptions, I4BGridData>[]> {
    return forkJoin([this.getGridsByMasterViewAndBusinessProfileId(masterView, businessProfileId), this.getPersonalGridByUserId(masterView, userId)]).pipe(
      map(([sharedGrids, userGrids]) => sharedGrids.concat(userGrids))
    );
  }

  duplicateAndShareGrid(
    gridToDuplicate: I4BGrid<I4BGridOptions, I4BGridData>,
    favoriteView: FavoriteView
  ): Observable<{ grid: I4BGrid<I4BGridOptions, I4BGridData>; favoriteView: FavoriteView }> {
    gridToDuplicate = { ...gridToDuplicate, businessProfileId: favoriteView.businessProfileId, id: undefined };
    return this.httpClient
      .post<I4BGrid<I4BGridOptions, I4BGridData>>(`${this.environment.api.url}${this.environment.api.endpoints?.grids}/${favoriteView.masterView}`, {
        ...gridToDuplicate,
        data: null
      })
      .pipe(map((grid) => ({ grid, favoriteView })));
  }

  shareGrid(
    grid: I4BGrid<I4BGridOptions, I4BGridData>,
    favoriteView: FavoriteView
  ): Observable<{ grid: I4BGrid<I4BGridOptions, I4BGridData>; favoriteView: FavoriteView }> {
    grid = { ...grid, businessProfileId: favoriteView.businessProfileId };
    return this.httpClient
      .put<I4BGrid<I4BGridOptions, I4BGridData>>(`${this.environment.api.url}/grids/${favoriteView.masterView}/${grid.id}`, {
        ...grid,
        data: null
      })
      .pipe(map((result) => ({ grid: result, favoriteView })));
  }
}
